import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { useConvenienceDispatchShiftTruckMutation } from 'src/hooks/useConvenienceDispatchShiftTruck.mutation';
import { PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryPlanningsDeliveries';
import { PLANNINGS_DRIVERS_QUERY_KEY } from 'src/hooks/useQueryPlanningsDrivers';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { useUpdateShiftTruckTruckNumberMutation } from 'src/hooks/useUpdateShiftTruckTruckNumber.mutation';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

import {
  collectLegRunIdsOfDelivery, InfiniteDeliveriesCache, patchDeliveryTruckNumber, patchLegRunTruckNumber,
} from './deliveriesCachePatch';
import { VehicleDisplayOverride } from './vehicleDisplayOverrides';

/**
 * 編集の対象は勤務 (shift_truck.id = delivery.id)。1 台に複数のドライバーが同じ時間帯で
 * 乗る勤務があるため、truckId から引くとどの行から開いても先頭の勤務が当たる。
 */
type EditAnchor = { anchor: HTMLElement; shiftTruckId: number };
type PlateAnchor = EditAnchor & { initialValue: string };

/** 車番の編集に必要な最小の delivery 情報。truckNumber は shift_trucks.truck_number */
type AssignableDelivery = Pick<PlanningsDeliveryWithLegRunEntity, 'id' | 'truckId' | 'truckNumber'>;

export type UseVehicleAssignmentEditingDeps = {
  rowGroups: VehicleRowGroup[];
  /** 勤務の実在確認に使う。配車前でも shift_truck があれば編集できる */
  deliveries: AssignableDelivery[];
  trucks: PlanningsTruckEntity[];
  drivers: PlanningsDriverEntity[];
  /** 変更後の再取得。画面ごとに対象クエリが違うので注入する */
  refetchAfterEdit: () => Promise<void>;
  notify: (message: string, variant: 'success' | 'error') => void;
  setVehicleOverride: (shiftTruckId: number, override: VehicleDisplayOverride) => void;
  clearVehicleOverride: (shiftTruckId: number) => void;
};

/**
 * 車両 / 車番 / ドライバーの差し替え。カード配車と /map で共有する。
 *
 * 3 つとも「上書きを置く → mutation → 再取得 → 上書きを消す」の順で、失敗しても
 * finally で上書きを消して元の値に戻す。この順序を画面ごとに書くと必ずずれる。
 */
export function useVehicleAssignmentEditing({
  rowGroups, deliveries, trucks, drivers, refetchAfterEdit, notify,
  setVehicleOverride, clearVehicleOverride,
}: UseVehicleAssignmentEditingDeps) {
  const { update: updateShiftTruck } = useConvenienceDispatchShiftTruckMutation();
  const updateShiftTruckTruckNumber = useUpdateShiftTruckTruckNumberMutation();
  const queryClient = useQueryClient();

  /**
   * 差し替え後の再取得。呼び出し側の refetch に加えてドライバー一覧の再取得も待つ。
   * 手入力で作った新しいドライバーは一覧に居らず、待たずに表示上書きを外すと
   * 名前が引けずに「ドライバー未設定」へ戻って見える。
   */
  const refetchAfterDriverEdit = useCallback(async () => {
    await Promise.all([
      refetchAfterEdit(),
      queryClient.refetchQueries({ queryKey: [PLANNINGS_DRIVERS_QUERY_KEY] }),
    ]);
  }, [refetchAfterEdit, queryClient]);

  /**
   * 更新できた車番をキャッシュへ書き込む。表示上書きは左リストの行グループにしか効かないため、
   * これをやらないと上書きを外した瞬間に古い車番へ戻り、再取得（/map は 9〜16 秒）が
   * 返るまでそのままになる。delivery 一覧のキーは全画面で共通なので、期間や
   * 固定ルートの違う分もまとめて prefix で当てる。
   *
   * leg_run 側も揃える。車番の表示は shift_truck → leg_run → truck の順に落ちるので、
   * leg_run に古い値が残ると空にしたはずの車番がフォールバックで復活する（/map は
   * deliveries の compact な legRun をこのキャッシュの値で上書きして表示を組む）。
   * API も同じ同期をしている (TruckNumbersController#sync_to_assigned_leg_runs)。
   */
  const applyTruckNumberToCache = useCallback((shiftTruckId: number, truckNumber: string) => {
    const legRunIds = new Set<number>();
    queryClient.setQueriesData(
      { queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] },
      (old: InfiniteDeliveriesCache<{
        id: number; truckNumber?: string | null; shipmentOrderLegRuns?: { id?: number | null }[];
      }> | undefined) => {
        collectLegRunIdsOfDelivery(old, shiftTruckId).forEach((id) => legRunIds.add(id));
        return patchDeliveryTruckNumber(old, shiftTruckId, truckNumber);
      },
    );
    if (legRunIds.size === 0) return;
    queryClient.setQueriesData(
      { queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] },
      (old: unknown) => patchLegRunTruckNumber(old, legRunIds, truckNumber),
    );
  }, [queryClient]);

  const [truckEditState, setTruckEditState] = useState<EditAnchor | null>(null);
  const [driverEditState, setDriverEditState] = useState<EditAnchor | null>(null);
  const [plateEditState, setPlateEditState] = useState<PlateAnchor | null>(null);
  const [plateInputValue, setPlateInputValue] = useState('');

  const findGroup = useCallback(
    (shiftTruckId: number | null) => (
      shiftTruckId == null ? null : rowGroups.find((g) => g.shiftTruckId === shiftTruckId) ?? null
    ),
    [rowGroups],
  );

  /**
   * そのグループ (= 勤務) に乗るドライバー。Vehicle.driverId は truck の代表値なので、
   * 1 台に 2 人が乗る勤務では 2 人目のグループでも 1 人目が返る。行の値を先に見る。
   */
  const driverIdOfGroup = useCallback(
    (group: VehicleRowGroup | null) => (
      group?.rows.map((r) => r.driverId).find((id) => id != null) ?? group?.vehicle.driverId ?? null
    ),
    [],
  );

  // 他の勤務が使っている truck / driver は候補から外す（同じ車両・同じ人を 2 つの勤務に
  // 割り当てられない）。編集中の勤務自身は除外対象にしない（今の値が候補から消えてしまう）
  const usedTruckIdsByOtherRows = useMemo(() => {
    if (!truckEditState) return new Set<number>();
    const editingTruckId = findGroup(truckEditState.shiftTruckId)?.vehicle.truckId ?? null;
    const set = new Set<number>();
    rowGroups.forEach((g) => {
      const tid = g.vehicle.truckId;
      if (tid != null && tid !== editingTruckId) set.add(tid);
    });
    return set;
  }, [rowGroups, truckEditState, findGroup]);

  const usedDriverIdsByOtherRows = useMemo(() => {
    if (!driverEditState) return new Set<number>();
    const set = new Set<number>();
    rowGroups.forEach((g) => {
      if (g.shiftTruckId === driverEditState.shiftTruckId) return;
      const driverId = driverIdOfGroup(g);
      if (driverId != null) set.add(driverId);
    });
    return set;
  }, [rowGroups, driverEditState, driverIdOfGroup]);

  const onTruckEdit = useCallback((shiftTruckId: number | null, anchor: HTMLElement) => {
    if (shiftTruckId == null) return;
    setTruckEditState({ anchor, shiftTruckId });
  }, []);

  const onDriverEdit = useCallback((shiftTruckId: number | null, anchor: HTMLElement) => {
    if (shiftTruckId == null) return;
    setDriverEditState({ anchor, shiftTruckId });
  }, []);

  const onPlateNumberEdit = useCallback((shiftTruckId: number | null, anchor: HTMLElement) => {
    if (shiftTruckId == null) return;
    // 車番は勤務ごとの値 (shift_trucks.truck_number)。Vehicle.plateNumber は truck 単位に
    // 作られるので、1 台に 2 つの勤務が乗ると 2 つ目を開いても 1 つ目の車番が入る
    const initial = deliveries.find((d) => d.id === shiftTruckId)?.truckNumber
      ?? findGroup(shiftTruckId)?.vehicle.plateNumber ?? '';
    setPlateInputValue(initial);
    setPlateEditState({ anchor, shiftTruckId, initialValue: initial });
  }, [deliveries, findGroup]);

  const handleTruckSelect = useCallback(async (newTruckId: number) => {
    const shiftTruckId = truckEditState?.shiftTruckId ?? null;
    const target = findGroup(shiftTruckId)?.vehicle ?? null;
    setTruckEditState(null);
    if (shiftTruckId == null || !target || target.truckId === newTruckId) return;
    const newTruck = trucks.find((t) => t.id === newTruckId);
    setVehicleOverride(shiftTruckId, {
      truckName: newTruck?.licensePlateValue ?? '',
      plateNumber: newTruck?.truckNumber ?? '',
    });
    try {
      await updateShiftTruck.mutateAsync({ shiftTruckId, truckId: newTruckId });
      await refetchAfterEdit();
      notify('車両を変更しました', 'success');
    } catch {
      notify('車両の変更に失敗しました', 'error');
    } finally {
      clearVehicleOverride(shiftTruckId);
    }
  }, [truckEditState, findGroup, trucks, updateShiftTruck, refetchAfterEdit, notify,
    setVehicleOverride, clearVehicleOverride]);

  const handleDriverSelect = useCallback(async (newDriverId: number) => {
    const shiftTruckId = driverEditState?.shiftTruckId ?? null;
    const currentDriverId = driverIdOfGroup(findGroup(shiftTruckId));
    setDriverEditState(null);
    if (shiftTruckId == null || currentDriverId === newDriverId) return;
    const newDriver = drivers.find((d) => d.id === newDriverId);
    setVehicleOverride(shiftTruckId, { driverName: newDriver?.name ?? '' });
    try {
      await updateShiftTruck.mutateAsync({ shiftTruckId, driverId: newDriverId });
      await refetchAfterDriverEdit();
      notify('ドライバーを変更しました', 'success');
    } catch {
      notify('ドライバーの変更に失敗しました', 'error');
    } finally {
      clearVehicleOverride(shiftTruckId);
    }
  }, [driverEditState, findGroup, driverIdOfGroup, drivers, updateShiftTruck, refetchAfterDriverEdit,
    notify, setVehicleOverride, clearVehicleOverride]);

  /**
   * マスタに無い名前を打ち込んで割り当てる。同名を探して無ければ作るのは API の担当
   * （FE で作ってから割り当てると、2 リクエストの間に落ちたとき宙に浮いた
   * ドライバーだけが残る）。
   */
  const handleDriverCreate = useCallback(async (name: string) => {
    const shiftTruckId = driverEditState?.shiftTruckId ?? null;
    setDriverEditState(null);
    const trimmed = name.trim();
    if (shiftTruckId == null || trimmed === '') return;
    setVehicleOverride(shiftTruckId, { driverName: trimmed });
    try {
      await updateShiftTruck.mutateAsync({ shiftTruckId, driverName: trimmed });
      await refetchAfterDriverEdit();
      notify('ドライバーを変更しました', 'success');
    } catch {
      notify('ドライバーの変更に失敗しました', 'error');
    } finally {
      clearVehicleOverride(shiftTruckId);
    }
  }, [driverEditState, updateShiftTruck, refetchAfterDriverEdit, notify,
    setVehicleOverride, clearVehicleOverride]);

  const handlePlateNumberSubmit = useCallback(async () => {
    const shiftTruckId = plateEditState?.shiftTruckId ?? null;
    const trimmed = plateInputValue.trim();
    const initial = plateEditState?.initialValue.trim();
    setPlateEditState(null);
    if (shiftTruckId == null || trimmed === initial) return;
    // 配車前 (legRunIds が空) でも shift_truck があれば編集できる
    if (!deliveries.some((d) => d.id === shiftTruckId)) return;
    setVehicleOverride(shiftTruckId, { plateNumber: trimmed });
    try {
      await updateShiftTruckTruckNumber.mutateAsync({ shiftTruckId, truckNumber: trimmed });
      // 先にキャッシュを新しい値にしてから再取得を投げる。待ってから上書きを外すと、
      // /map では 9〜16 秒のあいだ操作した行が固まって見える
      applyTruckNumberToCache(shiftTruckId, trimmed);
      notify('車番を更新しました', 'success');
      void refetchAfterEdit();
    } catch {
      notify('車番の更新に失敗しました', 'error');
    } finally {
      clearVehicleOverride(shiftTruckId);
    }
  }, [plateEditState, plateInputValue, deliveries, updateShiftTruckTruckNumber,
    refetchAfterEdit, notify, setVehicleOverride, clearVehicleOverride, applyTruckNumberToCache]);

  return {
    /** 車両リストへ渡すハンドラ */
    handlers: { onTruckEdit, onDriverEdit, onPlateNumberEdit },
    /** VehicleAssignmentMenus へそのまま渡す */
    menuProps: {
      trucks,
      drivers,
      truckAnchorEl: truckEditState?.anchor ?? null,
      currentTruckId: findGroup(truckEditState?.shiftTruckId ?? null)?.vehicle.truckId ?? null,
      excludeTruckIds: usedTruckIdsByOtherRows,
      onTruckClose: () => setTruckEditState(null),
      onTruckSelect: (id: number) => { void handleTruckSelect(id); },
      driverAnchorEl: driverEditState?.anchor ?? null,
      currentDriverId: driverIdOfGroup(findGroup(driverEditState?.shiftTruckId ?? null)),
      excludeDriverIds: usedDriverIdsByOtherRows,
      onDriverClose: () => setDriverEditState(null),
      onDriverSelect: (id: number) => { void handleDriverSelect(id); },
      onDriverCreate: (name: string) => { void handleDriverCreate(name); },
      plateAnchorEl: plateEditState?.anchor ?? null,
      plateValue: plateInputValue,
      onPlateChange: setPlateInputValue,
      onPlateClose: () => setPlateEditState(null),
      onPlateSubmit: () => { void handlePlateNumberSubmit(); },
    },
  };
}
